"use client";

import { useEffect, useMemo } from "react";

import {
  PHI_SIGNAL_VALUE_SCHEMAS,
  createPhiSignalAddress,
  type PhiSignalAddress,
  type PhiSignalRuntimeContext,
} from "../../types/signals";
import type { PhiCmsInstanceId } from "../../types/cms-instance-id";
import {
  usePhiSignalDispatcher,
  usePhiSignalListener,
  type PhiSignalInput,
} from "../runtime/runtime-signal-bus";
import { registerPhiSignalInstance } from "../runtime/runtime-signal-registry";
import { usePhiSignalRuntimePartition } from "../runtime/runtime-signal-partition";
import {
  PHI_FORM_SIGNAL_CHANNELS,
  createPhiRuntimeFormControllerAddress,
} from "./runtime-form-controller-signals";
import { readPhiRuntimeFormControllerWidgetId } from "./runtime-form-controller-requirement";
import {
  readPhiRuntimeFormFieldSignalValue,
  readPhiRuntimeFormTouchedSignalValue,
  readPhiRuntimeFormValiditySignalValue,
  readPhiRuntimeFormValuesSignalValue,
} from "./runtime-form-state";
import { isPhiRecord } from "../../helpers/is-record";
import { readPhiCsrfToken } from "../../helpers/csrf-token";
import { resolvePhiFormRelayArea } from "./form-relay-area";

export type PhiRuntimeFormSubmitSignalValue = {
  formId: string;
  phase: "submit" | "confirm";
  values: Record<string, unknown>;
};

export type PhiRuntimeFormResultSignalValue = {
  ok: boolean;
  /**
   * The transport's answer, where there was a transport.
   *
   * A Form kept local -- one that emits its values instead of submitting them -- finished without ever
   * asking anybody, and reporting a 200 for that would be inventing an answer nobody gave.
   */
  status?: number;
  payload: Record<string, unknown> | null;
};

export type PhiRuntimeFormErrorSignalValue = {
  message: string;
};

export type PhiRuntimeFormControllerMountProps = {
  address?: PhiSignalAddress | null;
  instanceKey?: string | number | null;
  context?: PhiSignalRuntimeContext | null;
  registerInstance?: boolean;
};

/**
 * Who the Controller's answer is for.
 *
 * The state a Form Controller accepts is echoed back so the Form can show it, and that echo used to
 * be a broadcast -- which nobody holds. The values for a Media asset were sent while the drawer was
 * still closed: the Controller's own signal waited for it to mount, as it should, and the echo it
 * made on arrival went out one render before the Form's binding had subscribed, into an empty room.
 * The Form opened blank, and its Save submitted nothing.
 *
 * Addressed to the Widget the Controller belongs to, the echo waits with everything else. A Form
 * Controller mounted for something other than a Widget has nobody to name and keeps broadcasting.
 */
function resolveFormOwnerReceiver(instanceKey: string | number | null | undefined) {
  const widgetId = readPhiRuntimeFormControllerWidgetId(instanceKey);
  return widgetId ? createPhiSignalAddress("cms", widgetId as PhiCmsInstanceId) : "broadcast";
}

function isPhiRuntimeFormSubmitSignalValue(value: unknown): value is PhiRuntimeFormSubmitSignalValue {
  if (!isPhiRecord(value) || !isPhiRecord(value.values)) {
    return false;
  }

  return (
    typeof value.formId === "string" &&
    (value.phase === "submit" || value.phase === "confirm")
  );
}

/**
 * One submit to the Site's form relay, carrying what the relay may not work out for itself.
 *
 * The Area the Form was drawn in, and the browser's CSRF token. The relay used to mint a CSRF pair of
 * its own for each submit, which proved nothing about the page that sent it; now it forwards this one,
 * and a handler that declares CSRF refuses a submit without it. Every submit carries the token, because
 * which handler needs one is the server's catalog, not the page's. A token that cannot be had fails the
 * submit here, with the token's own message (`readPhiCsrfToken` throws rather than returning null):
 * sent without it, the submit was refused by the handler and the Form showed a generic error that named
 * nothing.
 */
async function submitPhiFormToRelay(value: PhiRuntimeFormSubmitSignalValue, area: string | null) {
  const csrfToken = await readPhiCsrfToken();
  return fetch("/api/site/forms", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-csrf-token": csrfToken,
    },
    body: JSON.stringify({
      formId: value.formId,
      phase: value.phase,
      area,
      values: value.values,
    }),
  });
}

function emitControllerFeedback(
  dispatchSignal: ReturnType<typeof usePhiSignalDispatcher>,
  baseSignal: Pick<PhiSignalInput, "scope" | "sender" | "correlationId"> & {
    receiver: PhiSignalInput["receiver"];
  },
  input: Pick<PhiSignalInput, "channel" | "action" | "value" | "valueType" | "valueSchema">,
) {
  dispatchSignal({
    ...baseSignal,
    ...input,
    timestamp: Date.now(),
  });
}

export function PhiRuntimeFormControllerMount({
  address,
  instanceKey,
  context,
  registerInstance = true,
}: PhiRuntimeFormControllerMountProps) {
  const dispatchSignal = usePhiSignalDispatcher();
  const signalPartition = usePhiSignalRuntimePartition();
  const controllerAddress = useMemo(
    () => address ?? (instanceKey == null ? null : createPhiRuntimeFormControllerAddress(instanceKey)),
    [address, instanceKey],
  );
  const ownerReceiver = useMemo(() => resolveFormOwnerReceiver(instanceKey), [instanceKey]);

  usePhiSignalListener(
    (signal) => {
      if (!controllerAddress || signal.receiver !== controllerAddress) {
        return;
      }
      const isStateTransition =
        (signal.channel === PHI_FORM_SIGNAL_CHANNELS.values &&
          signal.valueSchema === PHI_SIGNAL_VALUE_SCHEMAS.formValues &&
          Boolean(readPhiRuntimeFormValuesSignalValue(signal.value))) ||
        (signal.channel === PHI_FORM_SIGNAL_CHANNELS.field &&
          signal.valueSchema === PHI_SIGNAL_VALUE_SCHEMAS.formField &&
          Boolean(readPhiRuntimeFormFieldSignalValue(signal.value))) ||
        (signal.channel === PHI_FORM_SIGNAL_CHANNELS.validity &&
          signal.valueSchema === PHI_SIGNAL_VALUE_SCHEMAS.formValidity &&
          Boolean(readPhiRuntimeFormValiditySignalValue(signal.value))) ||
        (signal.channel === PHI_FORM_SIGNAL_CHANNELS.touched &&
          signal.valueSchema === PHI_SIGNAL_VALUE_SCHEMAS.formTouched &&
          Boolean(readPhiRuntimeFormTouchedSignalValue(signal.value))) ||
        (signal.channel === PHI_FORM_SIGNAL_CHANNELS.dirty &&
          signal.valueType === "boolean");
      const isReset = signal.channel === PHI_FORM_SIGNAL_CHANNELS.reset && signal.action === "activate";
      const isClear = signal.channel === PHI_FORM_SIGNAL_CHANNELS.clear && signal.action === "clear";
      if ((isStateTransition && signal.action === "change") || isReset || isClear) {
        emitControllerFeedback(
          dispatchSignal,
          {
            scope: signal.scope,
            sender: controllerAddress,
            receiver: ownerReceiver,
            correlationId: signal.correlationId,
          },
          {
            channel: signal.channel,
            action: signal.action,
            value: signal.value,
            valueType: signal.valueType,
            valueSchema: signal.valueSchema,
          },
        );
        return;
      }
      if (
        signal.valueSchema !== PHI_SIGNAL_VALUE_SCHEMAS.formSubmit ||
        !isPhiRuntimeFormSubmitSignalValue(signal.value)
      ) {
        return;
      }
      if (
        !(
          (signal.channel === PHI_FORM_SIGNAL_CHANNELS.submit && signal.action === "activate") ||
          (signal.channel === PHI_FORM_SIGNAL_CHANNELS.confirm && signal.action === "activate")
        )
      ) {
        return;
      }

      const feedbackReceiver = signal.sender ?? "broadcast";
      const feedbackBase = {
        scope: signal.scope,
        sender: controllerAddress,
        receiver: feedbackReceiver,
        correlationId: signal.correlationId,
      } satisfies Pick<PhiSignalInput, "scope" | "sender" | "receiver" | "correlationId">;

      emitControllerFeedback(dispatchSignal, feedbackBase, {
        channel: PHI_FORM_SIGNAL_CHANNELS.submitting,
        action: "change",
        value: true,
        valueType: "boolean",
      });

      void submitPhiFormToRelay(signal.value, resolvePhiFormRelayArea(signalPartition))
        .then(async (response): Promise<PhiRuntimeFormResultSignalValue> => {
          const payload = (await response.json().catch(() => null)) as Record<string, unknown> | null;
          return {
            ok: response.ok,
            status: response.status,
            payload,
          };
        })
        .then((result) => {
          emitControllerFeedback(dispatchSignal, feedbackBase, {
            channel: PHI_FORM_SIGNAL_CHANNELS.result,
            action: "change",
            value: result,
            valueType: "json",
            valueSchema: PHI_SIGNAL_VALUE_SCHEMAS.formResult,
          });
        })
        .catch((error: unknown) => {
          emitControllerFeedback(dispatchSignal, feedbackBase, {
            channel: PHI_FORM_SIGNAL_CHANNELS.error,
            action: "change",
            value: {
              message: error instanceof Error ? error.message : "Form submit failed.",
            } satisfies PhiRuntimeFormErrorSignalValue,
            valueType: "json",
            valueSchema: PHI_SIGNAL_VALUE_SCHEMAS.formError,
          });
        })
        .finally(() => {
          emitControllerFeedback(dispatchSignal, feedbackBase, {
            channel: PHI_FORM_SIGNAL_CHANNELS.submitting,
            action: "change",
            value: false,
            valueType: "boolean",
          });
        });
    },
    controllerAddress
      ? {
          receiver: controllerAddress,
          context: context ?? undefined,
        }
      : null,
    controllerAddress,
  );

  useEffect(() => {
    if (!registerInstance || !controllerAddress) {
      return undefined;
    }

    return registerPhiSignalInstance(signalPartition, {
      address: controllerAddress,
      scope: "page",
      context: context ?? undefined,
    });
  }, [context, controllerAddress, registerInstance, signalPartition]);

  return null;
}
